import React, {useCallback} from 'react';

import {ErrorFlatButton} from 'src/components/error-flat-button';
import {FormInput} from 'src/components/input';
import {CustomValidatedControl} from 'src/contexts/validation-context';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FieldDescription} from '@pb/kit-component-field-description';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';

import type {AccessType, IpRangeKey} from '../typedef';
import type {IpRange} from 'src/openapi/admin/models/IpRange';


type Props = {
    id: number,
    range: IpRange,
    totalCount: number,
    onChangeRange: (id: number, value: string, key: IpRangeKey) => void,
    onChangeAccess: (id: number, key: AccessType, checked: boolean) => void,
    onRemove: (id: number) => void,
    disabled?: boolean,
};
export const IpRangeView: React.FC<Props> = props => {
    const {id, range, totalCount, onChangeRange, onChangeAccess, onRemove, disabled} = props;
    const {start, end, web, mobile, microSite} = range;

    const onSubmitStart = useCallback((value: string) => {
        onChangeRange(id, value, 'start');
    }, [id, onChangeRange]);

    const onSubmitEnd = useCallback((value: string) => {
        onChangeRange(id, value, 'end');
    }, [id, onChangeRange]);

    const onCheckBoxChange = useCallback((e: {event: React.SyntheticEvent<HTMLInputElement, Event>, name: string}) => {
        onChangeAccess(id, e.name as AccessType, e.event.currentTarget.checked);
    }, [id, onChangeAccess]);

    const onRemoveRange = useCallback(() => {
        onRemove(id);
    }, [id, onRemove]);

    return (
        <CustomValidatedControl name={`account-ip-range-${id}`}>
            {error => (
                <Group direction="v" gap="s" alignItems="center">
                    <Group gap="xl" alignItems="center">
                        <Item style={{width: 330}}>
                            <Group justify="space-between" alignItems="center">
                                <FormInput
                                    maxLength={15}
                                    readOnly={disabled}
                                    width={145}
                                    value={start}
                                    placeholder="0.0.0.0"
                                    onBlur={onSubmitStart}
                                    valid={error ? !error : undefined}
                                />
                                -
                                <FormInput
                                    maxLength={15}
                                    readOnly={disabled}
                                    width={145}
                                    value={end}
                                    placeholder="0.0.0.0"
                                    onBlur={onSubmitEnd}
                                    valid={error ? !error : undefined}
                                />
                            </Group>
                        </Item>
                        <Item style={{width: 330}}>
                            <Group gap="m" alignItems="center">
                                <CheckBox {...{label: 'Web', name: 'web', checked: !!web, onCheckBoxChange, disabled}}/>
                                <CheckBox {...{
                                    label: 'Mobile',
                                    name: 'mobile',
                                    checked: !!mobile,
                                    onCheckBoxChange,
                                    disabled,
                                }}
                                />
                                <CheckBox {...{
                                    label: 'Microsite',
                                    name: 'microSite',
                                    checked: !!microSite,
                                    onCheckBoxChange,
                                    disabled,
                                }}
                                />
                                {totalCount > 1 && !disabled &&
                                    <ErrorFlatButton icon={Icons.TIMES_CIRCLE} label="Remove" onClick={onRemoveRange}/>}
                            </Group>
                        </Item>
                    </Group>
                    {!!error && (
                        <FieldDescription aria-live="polite" color="Types.ERROR">
                            {error}
                        </FieldDescription>
                    )}
                </Group>
            )}
        </CustomValidatedControl>
    );
};
